/**
 * Utilities for checking Apple Pay availability and working with Telegram Mini App
 */

/**
 * Checks if the app is running in Telegram WebView
 */
export function isTelegramWebView(): boolean {
  if (typeof window === 'undefined') return false

  // Check for Telegram WebApp API presence
  return !!(window as any).Telegram?.WebApp
}

/**
 * Checks if Apple Pay is available on the device
 * Note: In Telegram WebView, Apple Pay may not work even if the device supports it
 */
export function isApplePayAvailable(): boolean {
  if (typeof window === 'undefined') return false

  // Check for ApplePaySession API presence
  if ((window as any).ApplePaySession) {
    // Check if the device can make payments via Apple Pay
    return (window as any).ApplePaySession.canMakePayments()
  }

  return false
}

/**
 * Checks if Apple Pay can work in the current environment
 * Returns an object with detailed information
 */
export function checkApplePaySupport(): {
  isSupported: boolean
  isTelegram: boolean
  canMakePayments: boolean
  shouldOpenInSafari: boolean
  reason?: string
} {
  const isTelegram = isTelegramWebView()
  const hasApplePayAPI = !!(window as any).ApplePaySession
  const canMakePayments = hasApplePayAPI
    ? (window as any).ApplePaySession.canMakePayments()
    : false

  // If in Telegram and Apple Pay is available - need to open in Safari
  if (isTelegram && canMakePayments) {
    return {
      isSupported: true,
      isTelegram: true,
      canMakePayments: true,
      shouldOpenInSafari: true,
      reason: 'Apple Pay requires opening in Safari for proper operation',
    }
  }

  // If in Telegram but Apple Pay is unavailable
  if (isTelegram && !canMakePayments) {
    return {
      isSupported: false,
      isTelegram: true,
      canMakePayments: false,
      shouldOpenInSafari: false,
      reason: 'Apple Pay is not available on this device',
    }
  }

  // If not in Telegram and Apple Pay is available - can be used
  if (!isTelegram && canMakePayments) {
    return {
      isSupported: true,
      isTelegram: false,
      canMakePayments: true,
      shouldOpenInSafari: false,
    }
  }

  // If not in Telegram and Apple Pay is unavailable
  return {
    isSupported: false,
    isTelegram: false,
    canMakePayments: false,
    shouldOpenInSafari: false,
    reason: 'Apple Pay is not available on this device',
  }
}

/**
 * Opens the current page in Safari (for iOS)
 * @param url - URL to open (defaults to the current URL)
 */
export function openInSafari(url?: string): void {
  const targetUrl = url || window.location.href

  // Try to open in Safari using various methods
  // 1. Use Telegram WebApp API if available
  if ((window as any).Telegram?.WebApp?.openLink) {
    ;(window as any).Telegram.WebApp.openLink(targetUrl)
    return
  }

  // 2. Standard method to open in a new window
  window.open(targetUrl, '_blank')
}

/**
 * Gets the current app URL for opening in Safari
 */
export function getCurrentAppUrl(): string {
  return window.location.href
}

/**
 * Checks if the payment method is Apple Pay
 */
export function isApplePayMethod(
  paymentMethod?: string | { paymentMethod: string },
): boolean {
  if (!paymentMethod) return false

  const method =
    typeof paymentMethod === 'string'
      ? paymentMethod
      : paymentMethod.paymentMethod

  return method === 'ApplePay' || method === 'apple-pay'
}

/**
 * Checks if the payment method is Google Pay
 */
export function isGooglePayMethod(
  paymentMethod?: string | { paymentMethod: string },
): boolean {
  if (!paymentMethod) return false

  const method =
    typeof paymentMethod === 'string'
      ? paymentMethod
      : paymentMethod.paymentMethod

  return method === 'GooglePay' || method === 'google-pay'
}
